import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import anime from "animejs";
import resume from "../../img/Pratyush_Kumar_Rai_Resume.pdf";
import "../styles/pages/home.scss";

const ROLES = [
  "Full-Stack Developer",
  "Defense Software Engineer @ BEL",
  "AI & Deep Learning Builder",
  "Distributed Systems & Mobile Engineer",
];

const STATS = [
  { value: "6+", target: 6, decimals: 0, suffix: "+", label: "Full-Stack, Mobile & AI Apps" },
  { value: "1", target: 1, decimals: 0, suffix: "", label: "Year Defense SWE at BEL (C++/Qt)" },
  { value: "8+", target: 8, decimals: 0, suffix: "+", label: "PostgreSQL Tables & Go/Node APIs" },
  { value: "0.9", target: 0.9, decimals: 1, suffix: "", label: "mAP Elsevier Published Research" },
  { value: "5", target: 5, decimals: 0, suffix: "", label: "SWE & AI Industry Credentials" },
];

function Home({ onToast, introFinished }) {
  const history = useHistory();

  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [displayedRole, setDisplayedRole] = useState(ROLES[0]);

  const [statsValues, setStatsValues] = useState(() =>
    STATS.map((s) => s.value)
  );

  // Dev button states
  const [emailCopied, setEmailCopied] = useState(false);
  const [resumeDownloadState, setResumeDownloadState] = useState("idle"); // idle | downloading | saved
  const [resumeProgress, setResumeProgress] = useState(0);

  const resumeLinkRef = useRef(null);
  const hasStatsAnimatedRef = useRef(false);

  const headingRef = useRef(null);

  const updateHeadingSpotlight = useCallback((clientX, clientY) => {
    if (!headingRef.current) return;
    const rect = headingRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    headingRef.current.style.setProperty("--mouse-x", `${x}px`);
    headingRef.current.style.setProperty("--mouse-y", `${y}px`);
    headingRef.current.style.setProperty("--spotlight-opacity", "1");
  }, []);

  const handleHeadingMouseMove = useCallback((e) => {
    updateHeadingSpotlight(e.clientX, e.clientY);
  }, [updateHeadingSpotlight]);

  const handleHeadingMouseEnter = useCallback((e) => {
    updateHeadingSpotlight(e.clientX, e.clientY);
  }, [updateHeadingSpotlight]);

  const handleHeadingMouseLeave = useCallback(() => {
    if (!headingRef.current) return;
    headingRef.current.style.setProperty("--spotlight-opacity", "0");
  }, []);


  // Continuous rotating roles typewriter
  useEffect(() => {
    let charIndex = 0;
    let isDeleting = false;
    let timeoutId;

    const tick = () => {
      const fullText = ROLES[currentRoleIndex];
      if (isDeleting) {
        setDisplayedRole(fullText.substring(0, charIndex - 1));
        charIndex--;
      } else {
        setDisplayedRole(fullText.substring(0, charIndex + 1));
        charIndex++;
      }

      let speed = isDeleting ? 30 : 55;

      if (!isDeleting && charIndex === fullText.length) {
        speed = 2200; // Pause at end of text for easy reading
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        setCurrentRoleIndex((prev) => (prev + 1) % ROLES.length);
        speed = 350;
      }

      timeoutId = setTimeout(tick, speed);
    };

    timeoutId = setTimeout(tick, 150);
    return () => clearTimeout(timeoutId);
  }, [currentRoleIndex]);

  // Stats counters: count up once when in view, then stay put
  useEffect(() => {
    const statCards = document.querySelectorAll(".hero-stat-card");
    if (!statCards.length) return;

    if (
      hasStatsAnimatedRef.current ||
      (typeof window !== "undefined" &&
        sessionStorage.getItem("portfolio_stats_animated") === "true")
    ) {
      setStatsValues(STATS.map((s) => s.value));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const anyVisible = entries.some((e) => e.isIntersecting);
        if (anyVisible && !hasStatsAnimatedRef.current) {
          hasStatsAnimatedRef.current = true;
          sessionStorage.setItem("portfolio_stats_animated", "true");

          STATS.forEach((stat, idx) => {
            const obj = { val: 0 };
            anime({
              targets: obj,
              val: stat.target,
              duration: 1200,
              easing: "easeOutExpo",
              update: () => {
                setStatsValues((prev) => {
                  const next = [...prev];
                  const numStr =
                    stat.decimals > 0
                      ? obj.val.toFixed(stat.decimals)
                      : Math.round(obj.val);
                  next[idx] = `${numStr}${stat.suffix}`;
                  return next;
                });
              },
              complete: () => {
                setStatsValues((prev) => {
                  const next = [...prev];
                  next[idx] = stat.value;
                  return next;
                });
              },
            });
          });

          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    statCards.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  // 1. DOWNLOAD RESUME: real PDF download with terminal status feedback
  const handleDownloadResume = useCallback(() => {
    if (resumeDownloadState !== "idle") return;

    if (resumeLinkRef.current) {
      resumeLinkRef.current.click();
    }

    setResumeDownloadState("downloading");
    setResumeProgress(35);

    setTimeout(() => {
      setResumeProgress(80);
    }, 350);

    setTimeout(() => {
      setResumeProgress(100);
      setResumeDownloadState("saved");
      if (onToast) onToast("📄 Resume downloaded (PDF) — Pratyush Kumar Rai");
    }, 750);

    setTimeout(() => {
      setResumeDownloadState("idle");
      setResumeProgress(0);
    }, 2800);
  }, [resumeDownloadState, onToast]);

  // 2. COPY EMAIL: robust clipboard and textarea fallback
  const handleCopyEmail = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      const email = "pratyushkumarrai3@gmail.com";

      const notifySuccess = () => {
        setEmailCopied(true);
        if (onToast) onToast("📋 Email copied: pratyushkumarrai3@gmail.com");
        setTimeout(() => setEmailCopied(false), 2400);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(email)
          .then(notifySuccess)
          .catch(() => fallbackCopy(email));
      } else {
        fallbackCopy(email);
      }

      function fallbackCopy(text) {
        try {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-9999px";
          textArea.style.top = "0";
          textArea.setAttribute("readonly", "");
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand("copy");
          document.body.removeChild(textArea);
          if (successful) notifySuccess();
        } catch (err) {
          console.warn("Clipboard fallback failed", err);
        }
      }
    },
    [onToast]
  );

  // Global Keyboard Shortcuts (P, R, E, C)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const targetTag = e.target && e.target.tagName ? e.target.tagName.toLowerCase() : "";
      const activeEl = document.activeElement;
      const activeTag = activeEl && activeEl.tagName ? activeEl.tagName.toLowerCase() : "";
      if (
        targetTag === "input" ||
        targetTag === "textarea" ||
        targetTag === "select" ||
        activeTag === "input" ||
        activeTag === "textarea" ||
        (activeEl && activeEl.isContentEditable)
      ) {
        return;
      }

      const key = e.key.toLowerCase();
      if (key === "p") {
        e.preventDefault();
        history.push("/portfolio");
      } else if (key === "r") {
        e.preventDefault();
        handleDownloadResume();
      } else if (key === "e") {
        e.preventDefault();
        handleCopyEmail();
      } else if (key === "c") {
        e.preventDefault();
        history.push("/contact");
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [history, handleDownloadResume, handleCopyEmail]);

  return (
    <section id="home" className="anchor">
      <div className="header-container">
        {/* HERO HEADING WITH BLUE ACCENT ON HOVER */}
        <h1
          ref={headingRef}
          className="hero-main-heading"
          onMouseMove={handleHeadingMouseMove}
          onMouseEnter={handleHeadingMouseEnter}
          onMouseLeave={handleHeadingMouseLeave}
        >
          {/* Base Layer: clean white text */}
          <div className="hero-heading-base">
            <span className="hero-title-line">
              <span>PRATYUSH</span>
            </span>
            <span className="hero-title-line">
              <span className="gradient-text">KUMAR RAI</span>
            </span>
          </div>

          {/* Interactive Spotlight Layer: only the part where mouse hovers turns electric blue */}
          <div className="hero-heading-spotlight" aria-hidden="true">
            <span className="hero-title-line">
              <span>PRATYUSH</span>
            </span>
            <span className="hero-title-line">
              <span>KUMAR RAI</span>
            </span>
          </div>
        </h1>

        {/* TERMINAL ROLE LINE: ROTATING ROLES WITH TYPEWRITER */}
        <div
          className="role-rotator-wrap"
          aria-label={`Role: ${ROLES[currentRoleIndex]}`}
          role="region"
        >
          <span className="terminal-prompt font-mono">~/role $</span>
          <span className="role-typed-wrap font-mono">
            <span className="role-typed">{displayedRole}</span>
            <span className="role-ghost" aria-hidden="true">
              Distributed Systems &amp; Mobile Engineer
            </span>
          </span>
          <span className="cursor-blink font-mono" aria-hidden="true">
            _
          </span>
        </div>

        {/* SHARP NARRATIVE SUBTEXT */}
        <p className="hero-subtext prose">
          Software Engineer building resilient distributed systems, mission-critical defense C++/Qt software at BEL, and modern AI/full-stack web applications.
        </p>

        {/* DEVELOPER COMMAND ROW: 4 BEAUTIFULLY STYLED DEV BUTTONS */}
        <div className="dev-command-row-wrap" role="region" aria-label="Quick Actions">
          <div className="dev-command-row">
            {/* 1. VIEW PROJECTS */}
            <a
              href="#/portfolio"
              className="dev-cmd-btn cta-buttons primary-cmd-btn"
              onClick={(e) => {
                e.preventDefault();
                history.push("/portfolio");
              }}
              aria-label="View Projects (press P)"
            >
              <div className="btn-content">
                <span className="prompt-char font-mono">$</span>
                <span className="cmd-text font-mono">cd ./projects</span>
                <span className="cursor-idle font-mono" aria-hidden="true">_</span>
                <span className="enter-hover font-mono" aria-hidden="true">↵ enter</span>
              </div>
              <kbd className="cmd-kbd font-mono" aria-hidden="true">P</kbd>
            </a>

            {/* 2. DOWNLOAD RESUME */}
            <a
              ref={resumeLinkRef}
              href={resume || "/Pratyush_Kumar_Rai_Resume.pdf"}
              download="Pratyush_Kumar_Rai_Resume.pdf"
              style={{ display: "none" }}
              aria-hidden="true"
              tabIndex={-1}
            >
              Hidden Resume Download Link
            </a>

            <button
              type="button"
              className={`dev-cmd-btn cta-buttons file-chip-btn ${
                resumeDownloadState !== "idle" ? "is-downloading" : ""
              }`}
              onClick={handleDownloadResume}
              aria-label="Download Resume PDF (press R)"
            >
              <div className="btn-content">
                {resumeDownloadState === "idle" && (
                  <>
                    <i className="fa fa-file-text-o file-icon" aria-hidden="true"></i>
                    <span className="file-name font-mono">resume.pdf</span>
                    <span className="file-tag font-mono">PDF</span>
                    <i className="fa fa-download download-arrow" aria-hidden="true"></i>
                  </>
                )}
                {resumeDownloadState === "downloading" && (
                  <span className="download-progress-text font-mono">
                    <i className="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
                    downloading… {resumeProgress}%
                  </span>
                )}
                {resumeDownloadState === "saved" && (
                  <span className="download-saved-text font-mono">
                    <span className="check-symbol">✓</span> saved
                  </span>
                )}
              </div>
              <kbd className="cmd-kbd font-mono" aria-hidden="true">R</kbd>
            </button>

            {/* 3. COPY EMAIL */}
            <button
              type="button"
              className={`dev-cmd-btn cta-buttons copy-cmd-btn ${
                emailCopied ? "is-copied" : ""
              }`}
              onClick={handleCopyEmail}
              aria-label="Copy email address (press E)"
            >
              <div className="btn-content">
                {emailCopied ? (
                  <span className="copied-status font-mono">
                    <span className="check-symbol">✓</span> copied to clipboard
                  </span>
                ) : (
                  <>
                    <span className="pkg-prefix font-mono">npm i</span>
                    <span className="pkg-email font-mono">pratyushkumarrai3@gmail.com</span>
                    <i className="fa fa-clone copy-icon" aria-hidden="true"></i>
                  </>
                )}
              </div>
              <kbd className="cmd-kbd font-mono" aria-hidden="true">E</kbd>
            </button>

            <span className="sr-only" aria-live="polite">
              {emailCopied ? "Email copied to clipboard" : ""}
            </span>

            {/* 4. CONTACT */}
            <a
              href="#/contact"
              className="dev-cmd-btn cta-buttons ssh-cmd-btn"
              onClick={(e) => {
                e.preventDefault();
                history.push("/contact");
              }}
              aria-label="Contact Pratyush (press C)"
            >
              <div className="btn-content">
                <span className="prompt-char font-mono">$</span>
                <span className="cmd-text font-mono">ssh contact@pratyush</span>
                <i className="fa fa-terminal ssh-icon" aria-hidden="true"></i>
              </div>
              <kbd className="cmd-kbd font-mono" aria-hidden="true">C</kbd>
            </a>
          </div>

          <div className="command-row-hint font-mono" aria-hidden="true">
            <span className="hint-label">tip: press</span>
            <kbd className="hint-kbd">P</kbd>
            <span className="hint-sep">/</span>
            <kbd className="hint-kbd">R</kbd>
            <span className="hint-sep">/</span>
            <kbd className="hint-kbd">E</kbd>
            <span className="hint-sep">/</span>
            <kbd className="hint-kbd">C</kbd>
          </div>
        </div>

        {/* COMPREHENSIVE STATS STRIP */}
        <div className="hero-stats-strip" role="region" aria-label="Key Accomplishments">
          {STATS.map((stat, i) => (
            <div key={i} className="hero-stat-card glass-card">
              <span className="stat-number font-display tabular-nums">
                {statsValues[i]}
              </span>
              <span className="stat-label font-mono">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Home;
